import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  DATA_LAYER_EVENT,
  DataLayer_BeginCheckout,
  DataLayer_CheckAddress,
  DataLayer_Event,
  DataLayer_SignUpFunnel_AccountDetails,
  DataLayer_SignUpFunnel_Complete,
  DataLayer_SignUpFunnel_DeliveryAddress,
  DataLayer_SignUpFunnel_Payload,
  DataLayer_User
} from './data-layer.types';
import { LocalStorageService } from '../local-storage.service';
import { Session } from '../types/session.type';
import { LOCALSTORAGE_KEYS } from '../constants/databases';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    dataLayer: any;
  }
}

interface SignupFunnelStepRecord {
  email: string;
  step_completed: string;
  event: string;
}

@Injectable({ providedIn: 'root' })
export class DataLayerService {

  #localStorageService = inject(LocalStorageService);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {}

  trackCheckAddressEvent(data: DataLayer_CheckAddress): void {
    this.#trackEvent<DataLayer_CheckAddress>({
      event: DATA_LAYER_EVENT.check_address,
      data
    });
  }

  trackBeginCheckoutEvent(data: DataLayer_BeginCheckout): void {
    const user = this.#isTrackEventValid();
    if (!user) return;
    this.#trackEvent<DataLayer_BeginCheckout>({
      event: DATA_LAYER_EVENT.begin_checkout,
      data,
      user
    });
  }

  trackSignUpFunnelDeliveryAddressEvent(data: DataLayer_SignUpFunnel_DeliveryAddress): void {
    this.#trackSignUpFunnelEvent(data, data.user.email);
  }

  trackSignUpFunnelAccountDetailsEvent(data: DataLayer_SignUpFunnel_AccountDetails): void {
    this.#trackSignUpFunnelEvent(data, data.user.email);
  }

  trackSignUpFunnelCompleteEvent(data: DataLayer_SignUpFunnel_Complete): void {
    this.#trackSignUpFunnelEvent(data, data.user.email);
  }

  #trackEvent<T>(info: DataLayer_Event<T>): void {
    try {
      if (!this.#isPlatformBrowser() || !environment.production) return;
      const payload: any = {
        event: info.event,
        ...(info.data as any)
      };
      if (info.user) payload.user = info.user;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (error) {
      console.log('Error Tracking DataLayer Event: ', info);
    }
  }

  #trackSignUpFunnelEvent(data: DataLayer_SignUpFunnel_Payload, email: string): void {
    try {
      const stepsCompleted: SignupFunnelStepRecord[] =
        this.#localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_FUNNEL_STEPS_COMPLETED) || [];

      const eventName = DATA_LAYER_EVENT.sign_up_funnel;
      const alreadyTracked = stepsCompleted.find(
        s => s.email === email && s.step_completed === data.funnel_step && s.event === eventName
      );
      if (alreadyTracked) return;

      if (!this.#isPlatformBrowser() || !environment.production) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...this.#withUserId(data) });

      // Record the step as tracked only after a successful push, so a skipped push
      // (SSR, non-prod) never permanently dedupes a legit event.
      stepsCompleted.push({ email, step_completed: data.funnel_step, event: eventName });
      this.#localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_FUNNEL_STEPS_COMPLETED, stepsCompleted);
    } catch (error) {
      console.log('Error Tracking DataLayer Sign Up Funnel Event: ', data);
    }
  }

  #withUserId(data: DataLayer_SignUpFunnel_Payload): DataLayer_SignUpFunnel_Payload {
    const userInfo = this.#getUserInfo();
    if (!userInfo?.id || (data.user as DataLayer_User).id) return data;
    return { ...data, user: { ...data.user, id: userInfo.id } } as DataLayer_SignUpFunnel_Payload;
  }

  #hasSession(): boolean {
    const currentSession: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    return !!currentSession?.accountInfo?.id;
  }

  #isPlatformBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  #getUserInfo(): DataLayer_User | null {
    const session: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!session?.accountInfo) return null;
    return {
      email: session.accountInfo.email,
      first_name: session.accountInfo.firstName,
      last_name: session.accountInfo.lastName,
      phone: session.accountInfo.phone,
      id: session.accountInfo.id.toString()
    };
  }

  #isTrackEventValid(): DataLayer_User | null {
    if (!this.#hasSession()) return null;
    return this.#getUserInfo();
  }
}
